import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";

const COOKIE_CONSENT_KEY = "studio_skandal_cookie_consent";

// Placeholder IDs — replace with real ones when ready
const GA_MEASUREMENT_ID = "";
const CLARITY_PROJECT_ID = "";

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    clarity: (...args: any[]) => void;
  }
}

const loadClarity = () => {
  if (!CLARITY_PROJECT_ID) return;
  if (document.querySelector(`script[src*="clarity.ms"]`)) return;
  
  window.clarity = window.clarity || function(...args: any[]) {
    (window.clarity as any).q = (window.clarity as any).q || [];
    (window.clarity as any).q.push(args);
  };
  
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
  document.head.appendChild(script);
};

const initGtagFunction = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(..._args: any[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
};

const setDefaultConsent = () => {
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
};

const loadGAScript = () => {
  if (!GA_MEASUREMENT_ID) return;
  if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) return;
  
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

const initGA = () => {
  if (!GA_MEASUREMENT_ID) return;
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID);
};

const grantConsent = () => {
  window.gtag("consent", "update", {
    analytics_storage: "granted",
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
  });
};

const denyConsent = () => {
  window.gtag("consent", "update", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
};

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    initGtagFunction();
    setDefaultConsent();
    loadGAScript();
    initGA();
    
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent === "accepted") {
      grantConsent();
      loadClarity();
    } else if (consent === "rejected") {
      denyConsent();
    } else {
      const timer = setTimeout(() => setIsVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    grantConsent();
    loadClarity();
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "rejected");
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-40 p-2 sm:p-4 pb-safe"
          >
            <div className="mx-2 sm:mx-4 lg:mx-auto lg:max-w-4xl">
              <div className="bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl p-4 sm:p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                  <div className="flex items-start sm:items-center gap-3 flex-1">
                    <div className="p-2 bg-primary/10 rounded-xl shrink-0 mt-0.5 sm:mt-0">
                      <Cookie className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Używamy plików cookies dla lepszych doświadczeń.{" "}
                        <Link 
                          to="/polityka-prywatnosci" 
                          className="text-primary hover:underline font-medium whitespace-nowrap"
                        >
                          Więcej
                        </Link>
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 w-full sm:w-auto">
                    <button
                      onClick={handleReject}
                      className="flex-1 sm:flex-none px-4 py-3 sm:py-2.5 text-sm text-zinc-400 hover:text-white border border-white/10 rounded-xl transition-colors min-h-[48px] sm:min-h-0"
                    >
                      Odrzuć
                    </button>
                    <Button
                      onClick={handleAccept}
                      className="flex-[2] sm:flex-none px-6 py-3 sm:py-2.5 h-auto bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl shadow-lg shadow-primary/25 min-h-[48px] sm:min-h-0"
                    >
                      Akceptuję
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
